import { IAuthUser } from './types';

export const appRoles = {
  SuperAdmin: 'SUPER_ADMIN',
  Admin: 'ADMIN',
  HubManager: 'HUB_MANAGER',
  Trainer: 'TRAINER',
  Instructor: 'INSTRUCTOR',
  Student: 'STUDENT',
};

export const isStudent = (user: IAuthUser): boolean => {
  if (!user) return false;
  const roles = user.roles || [];
  const perms = user.permissions || [];

  // Any explicit staff role → not a student
  const staffKeywords = [
    'admin',
    'principal',
    'trainer',
    'instructor',
    'manager',
    'super',
  ];
  if (roles.some((r) => staffKeywords.some((k) => r.toLowerCase().includes(k))))
    return false;

  // Any editing/management permission → not a student
  const staffPerms = [
    'STUDENT_EDIT',
    'USER_VIEW',
    'USER_EDIT',
    'ROLE_EDIT',
    'GROUP_EDIT',
    'COURSE_EDIT',
    'CLASS_EDIT',
  ];
  if (perms.some((p) => staffPerms.includes(p))) return false;

  // Explicit STUDENT role → student
  if (roles.some((r) => r.toLowerCase().includes('student'))) return true;

  // Has roles but none are student → not a student
  if (roles.length > 0) return false;

  // No roles: use permissions as the tiebreaker for legacy accounts.
  // Students have read-only permissions (DASHBOARD, COURSE_VIEW, CLASS_VIEW).
  // Staff editing permissions were already excluded above, so if there ARE
  // any permissions left this is a read-only account → student.
  // If there are NO permissions at all, we cannot determine the role —
  // default to false (not a student) to avoid misclassifying stale sessions.
  return perms.length > 0;
};

export const hasRole = (user: IAuthUser, appPermission: string): boolean => {
  // Non-student users (admin/trainer) can see all staff nav items.
  // Real data security is enforced by the server.
  if (!isStudent(user)) {
    return true;
  }
  const { permissions = [] } = user || {};
  return permissions?.indexOf(appPermission) > -1;
};

export const hasAnyRole = (
  user: IAuthUser,
  appPermissions: string[],
): boolean => appPermissions.filter(Boolean).some((it) => hasRole(user, it));

export const isInstructor = (user: IAuthUser) =>
  hasAnyRole(user, [appRoles.Instructor]);

export const isSuperAdmin = (user: IAuthUser): boolean => {
  if (!user) return false;
  const roles = user.roles || [];
  return roles.some((r) => r === 'SUPER_ADMIN');
};

export const isHubManager = (user: IAuthUser): boolean => {
  if (!user) return false;
  const roles = user.roles || [];
  return roles.some((r) => r === 'HUB_MANAGER');
};

export const isTrainer = (user: IAuthUser): boolean => {
  if (!user) return false;
  const roles = user.roles || [];
  if (roles.some((r) => r === 'TRAINER' || r === 'INSTRUCTOR')) return true;
  // Permission-based fallback for stale sessions: trainers have COURSE_EDIT +
  // CLASS_EDIT but NOT STUDENT_EDIT (which admins/hub managers have).
  const perms = user.permissions || [];
  return (
    perms.includes('COURSE_EDIT') &&
    perms.includes('CLASS_EDIT') &&
    !perms.includes('STUDENT_EDIT')
  );
};

export const isAdmin = (user: IAuthUser): boolean => {
  if (!user) return false;
  const roles = user.roles || [];
  if (
    roles.some((r) =>
      ['ADMIN', 'SUPER_ADMIN', 'PRINCIPAL'].includes(r.toUpperCase()),
    )
  )
    return true;
  // Permission-based fallback for stale sessions: ROLE_EDIT is only ever
  // granted to admin-tier roles (trainers/hub managers never have it).
  const perms = user.permissions || [];
  return perms.includes('ROLE_EDIT');
};

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  PRINCIPAL: 'Principal',
  HUB_MANAGER: 'Hub Manager',
  TRAINER: 'Instructor',
  INSTRUCTOR: 'Instructor',
  STUDENT: 'Student',
};

/** Human-readable name of the user's highest role ('' when none). */
export const roleLabel = (user: IAuthUser): string => {
  const roles = (user?.roles || []).map((r) => r.toUpperCase());
  const known = Object.keys(ROLE_LABELS).find((r) => roles.includes(r));
  return known ? ROLE_LABELS[known] : '';
};
